import Box from '@mui/material/Box';
import * as React from 'react';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import createDecorator from 'final-form-focus';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

import { ReduxState } from 'ducks';

import { InquiryInputForm } from './InquiryInputForm';
import {
  convertFormValuesToSwagger,
  getInitialValues,
  InquiryFormValues,
} from './InquiryFormValues';
import { InquiryConfirm } from './InquiryConfirm';
import { InquirySubmitted } from './InquirySubmitted';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { createInquiry } from 'ducks/client/inquiries';

const focusOnError = createDecorator<InquiryFormValues>();

export const InquiryForm = () => {
  const { t, i18n } = useTranslation();

  const [step, setStep] = React.useState<'INPUT_STEP' | 'CONFIRM_STEP' | 'FINISHED_STEP'>(
    'INPUT_STEP'
  );
  const reservation = useSelector((state: ReduxState) => state.server.reservation.reservation);

  const inquiryFeatureEnabled = useSelector(
    (state: ReduxState) =>
      state.server.settings.all?.subscriptions?.some(
        (subscription) => subscription.key === 'feature-inquiry' && subscription.status === 'ACTIVE'
      ) ?? false
  );

  const { apiKey } = React.useContext(ApiKeyContext);

  const router = useRouter();

  const dispatch = useDispatch();

  const initialValues = React.useMemo(() => getInitialValues(reservation), [
    reservation,
    t,
    router.query.r,
  ]);

  if (!inquiryFeatureEnabled) {
    return (
      <Box ml={3} mt={3}>
        <h3>{t('Page not found')}</h3>
      </Box>
    );
  }

  return (
    <Form<InquiryFormValues>
      initialValues={initialValues}
      keepDirtyOnReinitialize={true}
      decorators={[focusOnError]}
      onSubmit={async (values) => {
        try {
          switch (step) {
            case 'INPUT_STEP':
              setStep('CONFIRM_STEP');
              break;
            case 'CONFIRM_STEP':
              await dispatch(
                createInquiry(
                  apiKey,
                  convertFormValuesToSwagger(reservation?.id ?? '', values),
                  i18n.language
                )
              );
              setStep('FINISHED_STEP');
              break;
          }
        } catch (err) {
          return { [FORM_ERROR]: t('Save Failed') };
        }
      }}
    >
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          {step === 'INPUT_STEP' ? (
            <InquiryInputForm />
          ) : step === 'CONFIRM_STEP' ? (
            <InquiryConfirm onBackClick={() => setStep('INPUT_STEP')} />
          ) : (
            <InquirySubmitted />
          )}
        </form>
      )}
    </Form>
  );
};
